/**
 * Batch optimization data: slug sets + per-article assets.
 *
 * Split out of batchContentOptimizer.ts so the logic file stays focused.
 * See CONTENT_OPTIMIZATION_GUIDE.md for the methodology behind each asset.
 */

// Batch 22 + sequential Batch 23 slug sets + dateModified overrides are defined
// in their content modules and re-exported here so consumers import everything
// from a single module.
export { BATCH22_SLUGS, BATCH22_LASTUPDATED } from "@/lib/batch22Content";
export {
  BATCH23SEQ_SLUGS,
  BATCH23SEQ_LASTUPDATED,
} from "@/lib/batch23seqContent";
export { BATCH24_SLUGS, BATCH24_LASTUPDATED } from "@/lib/batch24Content";
export { BATCH25_SLUGS, BATCH25_LASTUPDATED } from "@/lib/batch25Content";
export { BATCH26_SLUGS, BATCH26_LASTUPDATED } from "@/lib/batch26Content";
export { BATCH27_SLUGS, BATCH27_LASTUPDATED } from "@/lib/batch27Content";
export { BATCH28_SLUGS, BATCH28_LASTUPDATED } from "@/lib/batch28Content";
export { BATCH29_SLUGS, BATCH29_LASTUPDATED } from "@/lib/batch29Content";

/** Slugs covered by Batch 1 (Compensation & Hiring cluster). */
export const BATCH1_SLUGS = new Set<string>([
  "how-to-headhunt-high-tier-system-specialists-in-the-philippines",
  "average-salary-for-virtual-assistant-in-the-philippines-2026",
  "how-much-to-pay-a-full-time-technical-va-in-philippines",
  "hourly-rate-vs-fixed-price-remote-workers-philippines",
  "hidden-costs-of-hiring-virtual-assistants-in-the-philippines",
  "how-to-calculate-savings-outsourcing-operations-to-the-philippines",
  "is-10-dollars-an-hour-a-good-salary-for-a-filipino-va",
  "how-to-handle-annual-bonuses-13th-month-pay-philippines-remote",
  "cost-comparison-us-marketing-agency-vs-filipino-technical-team",
  "how-to-structure-a-fair-compensation-package-for-filipino-contractors",
  "currency-exchange-risk-when-paying-remote-workers-in-php",
  "how-to-legally-hire-remote-contractor-philippines",
]);

/** Slugs covered by Batches 2 & 3 (info-gain assets only). */
export const BATCH23_SLUGS = new Set<string>([
  // Batch 2 — Hiring Strategy / platform cluster.
  "best-platform-hiring-specialized-automation-vas",
  "hire-direct-remote-contractor-vs-outsourcing-agency",
  "find-trusted-virtual-assistant-outside-upwork",
  "onlinejobs-ph-review-technical-hiring",
  "best-agencies-hire-filipino-virtual-assistants",
  "upwork-alternative-high-level-tech-virtual-assistants",
  "how-to-transition-an-assistant-from-task-taker-to-project-manager",
  "how-to-scaling-your-business-operations-using-remote-talent-leverage",
  "tips-for-seamless-communication-with-filipino-virtual-assistants",
  "how-to-build-an-internal-wiki-or-sop-library-for-your-team",
  "setting-kpis-and-performance-metrics-for-a-virtual-assistant",
  "how-to-prevent-communication-breakdown-with-offshore-team-members",
  // Batch 3 — Recruitment cluster.
  "how-to-avoid-recruitment-fees-when-hiring-from-the-philippines",
  "outsourcing-to-the-philippines-pros-and-cons-for-small-business",
  "how-to-recruit-direct-premium-filipino-freelancers",
  "is-virtualstaff-ph-safe-for-hiring-technical-vas",
  "how-to-find-independent-remote-contractors-in-the-philippines",
  "best-virtual-assistant-companies-in-the-philippines-reviews",
  "how-to-hire-a-virtual-assistant-in-the-philippines-without-an-agency",
  "onlinejobs-ph-vs-upwork-for-hiring-filipino-talent",
  "how-to-hire-independent-remote-contractor-safely",
  "where-to-find-premium-operations-virtual-assistants",
  "top-remote-hiring-platforms-b2b-business-owners",
  "verify-portfolio-credentials-offshore-freelancers",
]);

/** Slugs covered by Batch 5 (Legal & Security + Hiring cluster). */
export const BATCH5_SLUGS = new Set<string>([
  "how-to-interview-a-technical-virtual-assistant",
  "best-virtual-assistant-job-description-template",
  "how-to-handle-remote-assistant-accessing-client-data",
  "intellectual-property-protection-remote-freelance-contract",
  "how-to-monitor-remote-virtual-assistant-productivity-without-micromanaging",
  "virtual-assistant-compliance-and-data-security-guidelines",
  "how-to-draft-a-remote-contractor-agreement",
  "legal-risks-of-hiring-a-virtual-assistant-in-the-philippines",
  "best-password-management-tools-for-distributed-teams",
  "how-to-protect-business-data-when-hiring-overseas",
  "how-to-securely-share-passwords-with-a-remote-worker",
  "virtual-assistant-non-disclosure-agreement-template-download",
]);

/** Slugs covered by Batch 4 (Management & Communication / vetting cluster). */
export const BATCH4_SLUGS = new Set<string>([
  "best-project-management-tools-for-managing-remote-vas",
  "how-to-structure-daily-standups-with-a-remote-worker",
  "essential-productivity-tools-for-virtual-assistant-collaboration",
  "how-to-manage-virtual-assistants-across-different-time-zones",
  "how-to-vet-remote-freelancers-portfolio-and-case-studies",
  "onboarding-checklist-for-a-newly-hired-virtual-assistant",
  "how-to-choose-between-a-generalist-and-a-specialized-technical-assistant",
  "where-to-find-reliable-high-level-virtual-assistants",
  "how-to-test-a-virtual-assistant-technical-skills-before-hiring",
  "red-flags-when-hiring-a-remote-virtual-assistant",
  "how-to-run-a-paid-trial-for-a-virtual-assistant-candidate",
  "top-interview-questions-to-ask-a-remote-operations-assistant",
]);

/** Hiring-oriented slugs get a homepage contextual link (Batch 1 + Batch 4 + Batch 5 + Batch 6). */
export const HIRING_TOPIC_SLUGS = new Set<string>([
  "how-to-headhunt-high-tier-system-specialists-in-the-philippines",
  "how-to-legally-hire-remote-contractor-philippines",
  // Batch 4 — vetting / onboarding / hiring-decision topics.
  "how-to-vet-remote-freelancers-portfolio-and-case-studies",
  "onboarding-checklist-for-a-newly-hired-virtual-assistant",
  "how-to-choose-between-a-generalist-and-a-specialized-technical-assistant",
  "where-to-find-reliable-high-level-virtual-assistants",
  "how-to-test-a-virtual-assistant-technical-skills-before-hiring",
  "red-flags-when-hiring-a-remote-virtual-assistant",
  "how-to-run-a-paid-trial-for-a-virtual-assistant-candidate",
  "top-interview-questions-to-ask-a-remote-operations-assistant",
  // Batch 5 — hiring-decision / interview / compliance topics.
  "how-to-interview-a-technical-virtual-assistant",
  "best-virtual-assistant-job-description-template",
  "how-to-draft-a-remote-contractor-agreement",
  "legal-risks-of-hiring-a-virtual-assistant-in-the-philippines",
  // Batch 6 — hiring / delegation topics (founder never-delegate, fractional engineer hire).
  "what-tasks-should-a-founder-never-delegate-to-a-remote-worker",
  "hire-fractional-systems-engineer-philippines",
  // Batch 7 — hiring / sourcing / outsourcing-decision topics.
  "remote-revenue-operations-manager-philippines",
  "hire-enterprise-level-seo-architect-philippines",
  "can-you-outsource-crm-management-to-a-virtual-assistant",
  "b2b-sales-funnel-builder-philippines-freelance",
  "operations-automation-va-philippines-expert",
  "hire-professional-crm-manager-outsourced-philippines",
  "best-automation-freelancers-in-the-philippines",
  "hire-advanced-systems-architect-philippines",
  // Batch 8 — hiring / delegation / outsourcing-decision topics.
  "best-repetitive-tasks-to-outsource-to-a-va",
  "hire-technical-virtual-assistant-philippines-outsourcing",
  "how-to-audit-your-time-before-hiring-a-remote-worker",
  "what-to-delegate-to-an-operations-virtual-assistant",
  "tasks-a-virtual-assistant-can-do-list",
  // Batch 9 — hiring / operations-decision / VA-capability / ROI-of-outsourcing topics.
  "can-a-virtual-assistant-handle-business-operations-management",
  "when-should-a-small-business-owner-hire-a-va",
  "revops-virtual-assistant-philippines-expert",
  "how-to-calculate-roi-of-outsourcing-tasks",
  "freelance-virtual-assistant-vs-specialized-agency-pros-and-cons",
  "how-virtual-assistants-improve-business-productivity-statistics",
  // Batch 10 — hiring-decision / in-house-vs-outsourcing / startup-hiring topics.
  "hidden-costs-of-hiring-an-in-house-employee",
  "is-it-worth-hiring-a-virtual-assistant-for-a-startup",
  // Batch 11 — hire-specialist / hire-sequence-setup hiring-decision topics.
  "hire-hubspot-marketing-hub-specialist-philippines",
  "hire-professional-to-set-up-hubspot-sequences",
  // Batch 12 — technical-VA-hire / NDA / AI-VA-hire / password-share /
  // cost-comparison / executive-VA hiring-decision & security topics.
  "hire-technical-virtual-assistant-marketing-agencies",
  "virtual-assistant-non-disclosure-agreement-framework",
  "how-to-find-ai-savvy-virtual-assistants-philippines",
  "how-to-securely-share-passwords-overseas-remote-contractors",
  "cost-comparison-full-time-employee-vs-specialized-virtual-assistant",
  "executive-virtual-assistant-agency-owners",
  // Batch 13 — security / compliance / contractor-agreement / productivity-audit
  // hiring-decision & data-protection topics.
  "securely-share-passwords-overseas-remote-contractors",
  "protect-data-privacy-filipino-virtual-assistants",
  "gdpr-hipaa-compliant-virtual-assistant-philippines",
  "monitor-remote-contractor-productivity-without-micromanaging",
  "remote-contractor-agreement-templates-offshore-workers",
  "audit-virtual-assistant-weekly-logged-hours",
  // Batch 14 — RevOps-architect-hire / cost-comparison / signs-to-hire /
  // admin-hours-waste / revenue-operations-VA hiring-decision topics.
  "revops-architect-freelance-philippines-scale",
  "revenue-operations-virtual-assistant-growth",
  "signs-it-is-time-to-hire-a-virtual-assistant",
  "how-many-hours-a-week-does-a-business-owner-waste-on-admin-tasks",
  "cost-comparison-full-time-employee-vs-virtual-assistant",
  // Batch 15 — GHL-architect-hire / dedicated-HubSpot-manager-hire /
  // technical-VA-for-agencies-hire / revops-architect-hire hiring-decision topics.
  "hire-gohighlevel-architect-philippines-roi",
  "hire-dedicated-hubspot-manager-upwork-alternative",
  "technical-virtual-assistant-for-marketing-agencies",
  "revops-architect-freelance-philippines",
  // Batch 16 — compensation / payroll / contractor-classification / role-definition
  // hiring-decision & logistics topics.
  "how-to-pay-contractors-in-philippines-via-wise",
  "tax-obligations-hiring-remote-contractor-from-us",
  "standard-holiday-schedule-remote-workers-philippines",
  "how-to-structure-bonuses-incentives-remote-assistants",
  "best-currency-conversion-tool-paying-international-remote-teams",
  "w8ben-form-requirements-remote-independent-contractors",
  "how-to-structure-part-time-vs-full-time-remote-contract",
  "fair-salary-systems-virtual-assistant-philippines",
  "how-to-manage-international-wire-transfers-monthly-payroll",
  "difference-between-administrative-assistant-and-technical-va",
  // Batch 17 — hiring-credentials / hiring-decision / executive-VA-hire topics.
  "top-rated-automation-freelancers-philippines",
  "results-driven-virtual-assistant-case-studies",
  "executive-virtual-assistant-us-time-zone-shift",
  "best-hubspot-consultants-in-the-philippines",
  // Batch 18 — hire-GHL-VA / hire-technical-VA / hire-n8n-expert hiring-decision topics.
  "hire-gohighlevel-virtual-assistant-philippines",
  "hire-technical-virtual-assistant-philippines",
  "hire-n8n-automation-expert-philippines",
  // Batch 19 — vetting / sourcing / direct-hire / part-time-vs-full-time /
  // no-agency-fee / independent-contract hiring-decision topics.
  "secure-data-compliant-virtual-assistant",
  "high-tier-virtual-assistant-portfolio",
  "reliable-remote-operations-assistant",
  "english-fluent-virtual-assistant-philippines",
  "vetted-technical-virtual-assistant",
  "freelance-technical-virtual-assistant-direct",
  "hire-independent-contract-virtual-assistant",
  "remote-hubspot-operations-manager-philippines",
  "dedicated-virtual-assistant-no-agency-fee",
  "part-time-operations-virtual-assistant",
  "full-time-technical-remote-assistant",
  "hire-direct-virtual-assistant-philippines",
  // Batch 20 — tech-stack / SaaS-onboarding / HubSpot-admin / Zapier & n8n VA /
  // GHL & CRM-database VA / B2B-funnel VA / premium-contractor / HubSpot-expert
  // VA / executive-VA hiring-decision topics.
  "tech-stack-management-freelancer",
  "saas-onboarding-virtual-assistant",
  "hubspot-certified-administrator-freelancer-philippines",
  "zapier-workflow-virtual-assistant",
  "n8n-automation-virtual-assistant",
  "hubspot-certified-virtual-assistant",
  "gohighlevel-specialist-virtual-assistant",
  "crm-database-virtual-assistant",
  "b2b-sales-funnel-virtual-assistant",
  "hire-premium-independent-remote-contractor",
  "technical-virtual-assistant-hubspot-expert",
  "executive-virtual-assistant-for-agency-owners",
  // Batch 21 — project-management-VA / specialized-automation-VA / technical-VA-
  // freelance / fractional-systems-manager / operations-VA / outsourcing-niches /
  // HubSpot-specialist / vetting / system-architect / RevOps-outsourcing /
  // GHL-architect / HubSpot-admin hiring-decision topics.
  "project-management-virtual-assistant",
  "hire-specialized-automation-va",
  "technical-virtual-assistant-freelance",
  "fractional-systems-manager-remote",
  "hire-operations-virtual-assistant",
  "best-niches-for-outsourcing-high-level-technical-work-to-the-philippines",
  "hire-hubspot-specialist-philippines",
  "how-to-vet-advanced-technical-problem-solving-skills-remote-candidates",
  "hiring-a-remote-system-architect-in-the-philippines",
  "outsource-revenue-operations-revops-to-the-philippines",
  "hire-gohighlevel-architect-philippines",
  "how-to-hire-a-remote-hubspot-certified-administrator-in-the-philippines",
  // Batch 22 — "-v2" hiring-decision / compensation / sourcing topics.
  "cost-comparison-fte-vs-specialized-va-v2",
  "pay-independent-contractors-philippines-wise-v2",
  "tax-obligations-hiring-remote-contractor-us-v2",
  "w8ben-form-requirements-remote-independent-contractors-v2",
  "hidden-costs-hiring-virtual-assistants-philippines-v2",
  "manage-filipino-contractors-different-time-zones-v2",
  "signs-time-to-outsource-business-operations-manager-v2",
  "fair-compensation-package-specialized-filipino-tech-talent-v2",
  "remote-virtual-assistant-vs-software-outsourcing-agency-v2",
  "how-to-find-ai-savvy-virtual-assistants-philippines-v2",
  // Sequential Batch 23 — hiring-strategy decision topics.
  "when-to-hire-specialized-tech-assistant-vs-generalist",
  "what-skills-to-look-for-in-crm-virtual-assistant",
  "can-general-virtual-assistant-build-automation-workflows",
  "hiring-virtual-assistant-manage-advanced-software-stacks",
  // Batch 24 — programmer-vs-automation / technical-VA requirements /
  // systems-engineering failure / general-to-technical transition / tech-VA
  // rate / performance tracking / KPIs / daily reporting / weekly syncs /
  // contractor accountability / trust building / async communication
  // hiring-decision & management topics.
  "should-i-hire-programmer-or-automation-specialist-remote",
  "technical-virtual-assistant-job-requirements-checklist",
  "why-general-virtual-assistants-fail-at-systems-engineering",
  "how-to-transition-business-from-general-va-to-technical-va",
  "average-hourly-rate-technical-virtual-assistant-philippines",
  "how-to-track-remote-virtual-assistant-performance-without-tracking-software",
  "best-key-performance-indicators-kpis-for-operations-assistant",
  "how-to-set-up-a-daily-reporting-system-for-remote-workers",
  "how-to-structure-weekly-sync-calls-with-an-offshore-assistant",
  "managing-remote-contractors-accountability-and-delivery-deadlines",
  "how-to-build-trust-with-a-newly-hired-virtual-assistant",
  "best-asynchronous-communication-framework-for-remote-distributed-teams",
  // Batch 25 — missed-deadline recovery / weekly-hours audit / measurable
  // expectations / SOP creation / video-onboarding tools / Notion training
  // wiki / infrastructure streamlining / client-onboarding training /
  // technical-PM delegation / automated documentation / calendar-email handover
  // / fractional remote infrastructure hiring-decision & management topics.
  "what-to-do-when-a-remote-virtual-assistant-misses-a-deadline",
  "how-to-audit-a-virtual-assistants-weekly-log-hours",
  "setting-measurable-expectations-for-a-remote-systems-manager",
  "how-to-create-standard-operating-procedures-sops-for-a-va",
  "best-tools-to-record-video-instructions-for-remote-onboarding",
  "how-to-build-a-company-training-wiki-in-notion-for-team",
  "streamlining-company-infrastructure-before-bringing-on-remote-talent",
  "how-to-train-a-virtual-assistant-to-handle-client-onboarding",
  "how-to-delegate-technical-project-management-to-an-assistant",
  "building-an-automated-documentation-library-for-internal-processes",
  "how-to-hand-over-your-calendar-and-email-cleanly-to-a-va",
  "scale-business-operations-using-fractional-remote-infrastructure",
  // Batch 26 — role/hire topics (4 of 12) → homepage contextual link.
  // The other 8 are platform topics → service-page links (contextualLinks.ts).
  "hire-technical-va-gohighlevel",
  "operations-automation-va-philippines",
  "b2b-sales-funnel-builder-philippines",
  "advanced-crm-virtual-assistant",
  // Batch 27 — hiring/operations topic (1 of 12) → homepage contextual link.
  // The other 11 are HubSpot platform topics → service-page links.
  "how-to-make-your-business-remote-ready-for-immediate-hiring",
  // Batch 28 — hiring/role topics (3 of 12) → homepage contextual link.
  // The other 9 are platform topics → service-page links (contextualLinks.ts).
  "hire-someone-to-connect-n8n-to-gohighlevel",
  "reduce-agency-admin-work-with-automation-va",
  "best-technical-virtual-assistants-for-marketing-agencies",
]);

/** Slugs covered by Batch 6 (Operations & delegation / HubSpot data-hygiene cluster). */
export const BATCH6_SLUGS = new Set<string>([
  "how-to-organize-business-tasks-for-an-assistant-onboarding",
  "examples-of-virtual-assistant-workflows-for-small-businesses",
  "what-tasks-should-a-founder-never-delegate-to-a-remote-worker",
  "how-to-document-processes-before-outsourcing",
  "hire-fractional-systems-engineer-philippines",
  "how-to-clean-up-bloated-hubspot-database",
  "hubspot-portal-audit-specialist-philippines",
  "hire-expert-reduce-hubspot-contact-tier-costs",
  "hubspot-crm-database-deduplication-freelancer",
  "fix-unmapped-custom-properties-hubspot",
  "hubspot-data-migration-consultant-remote",
  "clean-up-messy-hubspot-portal-freelance",
]);

/** Slugs covered by Batch 7 (HubSpot architecture / Talent Arbitrage / Delegation cluster). */
export const BATCH7_SLUGS = new Set<string>([
  "outsource-hubspot-data-hygiene-specialist-philippines",
  "hubspot-lifecycle-stage-mapping-expert",
  "how-to-structure-hubspot-custom-objects",
  "remote-revenue-operations-manager-philippines",
  "hire-enterprise-level-seo-architect-philippines",
  "can-you-outsource-crm-management-to-a-virtual-assistant",
  "b2b-sales-funnel-builder-philippines-freelance",
  "operations-automation-va-philippines-expert",
  "hire-professional-crm-manager-outsourced-philippines",
  "virtual-assistant-tasks-for-busy-agency-owners",
  "best-automation-freelancers-in-the-philippines",
  "hire-advanced-systems-architect-philippines",
]);

/** Slugs covered by Batch 8 (GoHighLevel architecture / Delegation / Meta Ads tracking cluster). */
export const BATCH8_SLUGS = new Set<string>([
  "best-repetitive-tasks-to-outsource-to-a-va",
  "gohighlevel-specialist-philippines-arbitrage",
  "hire-technical-virtual-assistant-philippines-outsourcing",
  "behavioral-trigger-mapping-expert-freelance",
  "how-to-audit-your-time-before-hiring-a-remote-worker",
  "white-label-gohighlevel-snapshot-developer",
  "how-to-manage-100-plus-ghl-sub-accounts",
  "what-to-delegate-to-an-operations-virtual-assistant",
  "meta-conversion-api-tracking-setup-multiple-locations-guide",
  "setup-lead-scoring-model-in-gohighlevel",
  "tasks-a-virtual-assistant-can-do-list",
  "automated-asset-collection-workflow-for-agency-clients",
]);

/** Slugs covered by Batch 9 (Client Retention / Operations / Lead-to-Cash / Meta CAPI / GHL multi-account cluster). */
export const BATCH9_SLUGS = new Set<string>([
  "reduce-high-ticket-agency-churn-with-automation",
  "can-a-virtual-assistant-handle-business-operations-management",
  "how-to-automate-client-onboarding-marketing-agency",
  "lead-to-cash-automation-specialist-freelance",
  "when-should-a-small-business-owner-hire-a-va",
  "revops-virtual-assistant-philippines-expert",
  "how-to-calculate-roi-of-outsourcing-tasks",
  "meta-conversion-api-tracking-setup-multiple-locations",
  "freelance-virtual-assistant-vs-specialized-agency-pros-and-cons",
  "manage-100-plus-sub-accounts-gohighlevel",
  "how-virtual-assistants-improve-business-productivity-statistics",
  "reduce-agency-admin-work-80-percent",
]);

/** Slugs covered by Batch 10 (Lead Response / Churn / Multi-Account Scale / Meta CAPI / Sales-Psychology cluster). */
export const BATCH10_SLUGS = new Set<string>([
  "meta-conversion-api-tracking-setup-for-multiple-locations",
  "how-to-manage-100-plus-sub-accounts-in-ghl",
  "behavioral-trigger-mapping-expert-timing",
  "reduce-agency-churn-automated-onboarding",
  "hidden-costs-of-hiring-an-in-house-employee",
  "automated-lead-response-workflow-under-30-seconds",
  "sales-psychology-automation-freelancer-intent",
  "how-to-reduce-agency-admin-work-by-80-percent",
  "lead-to-cash-automation-specialist-roi",
  "how-to-reduce-agency-churn-with-automated-onboarding",
  "is-it-worth-hiring-a-virtual-assistant-for-a-startup",
  "automated-asset-collection-workflow-for-agencies",
]);

/** Slugs covered by Batch 11 (HubSpot architecture / n8n orchestration / Voice-AI-BPO cluster). */
export const BATCH11_SLUGS = new Set<string>([
  "alternative-to-traditional-bpo-call-centers-in-the-philippines",
  "hire-hubspot-marketing-hub-specialist-philippines",
  "how-to-build-advanced-hubspot-workflows",
  "hubspot-lead-scoring-model-setup-freelancer",
  "automated-lead-rotation-logic-hubspot-architect",
  "hire-professional-to-set-up-hubspot-sequences",
  "hubspot-multi-branch-workflow-architect-philippines",
  "behavioral-trigger-mapping-hubspot-freelance",
  "setup-hubspot-smart-content-personalized-funnels",
  "outsource-hubspot-newsletter-automation-setup",
  "hubspot-multi-touch-attribution-reporting-expert",
  "connect-hubspot-to-n8n-workflow-developer",
]);

/** Slugs covered by Batch 12 (HubSpot API / Vapi-Voice-AI / technical-VA hiring / CRM automation / Zapier+n8n cluster). */
export const BATCH12_SLUGS = new Set<string>([
  "hubspot-api-integration-specialist-philippines",
  "how-to-securely-connect-vapi-voice-ai-hubspot-n8n",
  "hire-technical-virtual-assistant-marketing-agencies",
  "virtual-assistant-non-disclosure-agreement-framework",
  "how-to-find-ai-savvy-virtual-assistants-philippines",
  "crm-automation-virtual-assistant-philippines",
  "how-to-securely-share-passwords-overseas-remote-contractors",
  "cost-comparison-full-time-employee-vs-specialized-virtual-assistant",
  "fractional-revenue-operations-revops-architect-remote",
  "executive-virtual-assistant-agency-owners",
  "remote-hubspot-certified-workflow-manager",
  "zapier-and-n8n-integration-specialist-remote",
]);

/** Slugs covered by Batch 13 (Technical Operations / Security / Compliance cluster). */
export const BATCH13_SLUGS = new Set<string>([
  "virtual-assistant-set-up-sales-funnels",
  "expert-fix-broken-crm-webhooks",
  "outsource-website-maintenance-plugin-management",
  "lead-scoring-model-setup-virtual-assistant",
  "b2b-email-deliverability-spf-tracking-specialist",
  "securely-share-passwords-overseas-remote-contractors",
  "protect-data-privacy-filipino-virtual-assistants",
  "gdpr-hipaa-compliant-virtual-assistant-philippines",
  "best-asynchronous-communication-framework-remote-teams",
  "monitor-remote-contractor-productivity-without-micromanaging",
  "remote-contractor-agreement-templates-offshore-workers",
  "audit-virtual-assistant-weekly-logged-hours",
]);

/** Slugs covered by Batch 14 (RevOps / AI Automation / Voice AI / Hiring-Decision cluster). */
export const BATCH14_SLUGS = new Set<string>([
  "revenue-operations-virtual-assistant-growth",
  "revops-architect-freelance-philippines-scale",
  "cost-comparison-full-time-employee-vs-virtual-assistant",
  "ai-objection-handling-workflow-setup-close",
  "llm-integrated-automation-specialist-data",
  "how-many-hours-a-week-does-a-business-owner-waste-on-admin-tasks",
  "lead-scoring-model-setup-freelancer",
  "agentic-ai-workflow-developer-freelancer-future",
  "behavioral-trigger-mapping-expert",
  "twilio-voice-ai-automation-developer-scale",
  "signs-it-is-time-to-hire-a-virtual-assistant",
  "vapi-voice-ai-integration-specialist-inbound",
]);

/** Slugs covered by Batch 15 (RevOps / CRM Reliability / HubSpot Enterprise / GHL
 * Architecture / AI Automation / Hiring-Decision cluster). */
export const BATCH15_SLUGS = new Set<string>([
  "sales-psychology-automation-freelancer",
  "crm-error-handling-fallbacks-developer-safety",
  "lead-to-cash-automation-specialist",
  "enterprise-hubspot-systems-architect-philippines",
  "technical-virtual-assistant-for-marketing-agencies",
  "hire-dedicated-hubspot-manager-upwork-alternative",
  "revenue-operations-virtual-assistant",
  "revops-architect-freelance-philippines",
  "hubspot-marketing-automation-developer-philippines",
  "ghl-custom-values-fields-specialist",
  "ai-objection-handling-workflow-setup",
  "hire-gohighlevel-architect-philippines-roi",
]);

/** Slugs covered by Batch 16 (Compensation & Logistics / Hiring Strategy /
 * Security cluster). Truthful publish dates preserved; dateModified via
 * BATCH16_LASTUPDATED. */
export const BATCH16_SLUGS = new Set<string>([
  "handling-remote-assistant-access-sensitive-client-crm-data",
  "how-to-pay-contractors-in-philippines-via-wise",
  "tax-obligations-hiring-remote-contractor-from-us",
  "how-to-handle-timezone-differences-filipino-assistant",
  "standard-holiday-schedule-remote-workers-philippines",
  "how-to-structure-bonuses-incentives-remote-assistants",
  "best-currency-conversion-tool-paying-international-remote-teams",
  "w8ben-form-requirements-remote-independent-contractors",
  "how-to-structure-part-time-vs-full-time-remote-contract",
  "fair-salary-systems-virtual-assistant-philippines",
  "how-to-manage-international-wire-transfers-monthly-payroll",
  "difference-between-administrative-assistant-and-technical-va",
]);

/** Batch 16 dateModified overrides (3-day backward from 2025-04-06). */
export const BATCH16_LASTUPDATED: Record<string, string> = {
  "handling-remote-assistant-access-sensitive-client-crm-data": "2025-04-06",
  "how-to-pay-contractors-in-philippines-via-wise": "2025-04-03",
  "tax-obligations-hiring-remote-contractor-from-us": "2025-03-31",
  "how-to-handle-timezone-differences-filipino-assistant": "2025-03-28",
  "standard-holiday-schedule-remote-workers-philippines": "2025-03-25",
  "how-to-structure-bonuses-incentives-remote-assistants": "2025-03-22",
  "best-currency-conversion-tool-paying-international-remote-teams":
    "2025-03-19",
  "w8ben-form-requirements-remote-independent-contractors": "2025-03-16",
  "how-to-structure-part-time-vs-full-time-remote-contract": "2025-03-13",
  "fair-salary-systems-virtual-assistant-philippines": "2025-03-10",
  "how-to-manage-international-wire-transfers-monthly-payroll": "2025-03-07",
  "difference-between-administrative-assistant-and-technical-va": "2025-03-04",
};

/** Slugs covered by Batch 17 (AI/LLM Automation / GHL API & Multi-Account /
 * HubSpot Consultants & Support / Voice AI / Hiring-Credentials cluster).
 * Owner-verified publish dates (2025-02-10 → 2025-04-06, 5-day intervals);
 * dateModified via BATCH17_LASTUPDATED (2025-03-01 → 2025-01-07, 3-day backward). */
export const BATCH17_SLUGS = new Set<string>([
  "llm-integrated-automation-specialist",
  "gohighlevel-api-integration-specialist-custom",
  "outsource-hubspot-support-specialist-remote",
  "ghl-multi-account-management-expert-growth",
  "agentic-ai-workflow-developer-freelancer",
  "twilio-voice-ai-automation-developer",
  "best-hubspot-consultants-in-the-philippines",
  "vapi-voice-ai-integration-specialist",
  "executive-virtual-assistant-us-time-zone-shift",
  "results-driven-virtual-assistant-case-studies",
  "top-rated-automation-freelancers-philippines",
  "fractional-hubspot-revops-architect",
]);

/** Batch 17 dateModified overrides (3-day backward from 2025-03-01). */
export const BATCH17_LASTUPDATED: Record<string, string> = {
  "llm-integrated-automation-specialist": "2025-03-01",
  "gohighlevel-api-integration-specialist-custom": "2025-02-26",
  "outsource-hubspot-support-specialist-remote": "2025-02-21",
  "ghl-multi-account-management-expert-growth": "2025-02-16",
  "agentic-ai-workflow-developer-freelancer": "2025-02-11",
  "twilio-voice-ai-automation-developer": "2025-02-06",
  "best-hubspot-consultants-in-the-philippines": "2025-02-01",
  "vapi-voice-ai-integration-specialist": "2025-01-27",
  "executive-virtual-assistant-us-time-zone-shift": "2025-01-22",
  "results-driven-virtual-assistant-case-studies": "2025-01-17",
  "top-rated-automation-freelancers-philippines": "2025-01-12",
  "fractional-hubspot-revops-architect": "2025-01-07",
};

/** Slugs covered by Batch 18 (GHL Architecture / n8n Workflow / API Integration /
 * Revenue Engine / Hiring cluster). These carried fabricated future dates
 * (2026); owner-verified publish dates 2025-04-11 → 2025-06-05 (5-day
 * intervals continuing Batch 17); dateModified via BATCH18_LASTUPDATED
 * (2025-01-04 → 2024-12-02, 3-day backward continuing Batch 17). */
export const BATCH18_SLUGS = new Set<string>([
  "white-label-gohighlevel-setup-freelancer",
  "crm-error-handling-and-fallbacks-developer",
  "ghl-custom-values-and-fields-specialist",
  "custom-api-integration-virtual-assistant",
  "connect-gohighlevel-to-n8n-specialist",
  "n8n-webhook-engineering-consultant",
  "hire-n8n-automation-expert-philippines",
  "n8n-workflow-developer-freelance",
  "anatomy-of-a-revenue-engine",
  "hire-gohighlevel-virtual-assistant-philippines",
  "ghl-automation-expert-philippines",
  "hire-technical-virtual-assistant-philippines",
]);

/** Batch 18 dateModified overrides (3-day backward from 2025-01-04). */
export const BATCH18_LASTUPDATED: Record<string, string> = {
  "white-label-gohighlevel-setup-freelancer": "2025-01-04",
  "crm-error-handling-and-fallbacks-developer": "2025-01-01",
  "ghl-custom-values-and-fields-specialist": "2024-12-29",
  "custom-api-integration-virtual-assistant": "2024-12-26",
  "connect-gohighlevel-to-n8n-specialist": "2024-12-23",
  "n8n-webhook-engineering-consultant": "2024-12-20",
  "hire-n8n-automation-expert-philippines": "2024-12-17",
  "n8n-workflow-developer-freelance": "2024-12-14",
  "anatomy-of-a-revenue-engine": "2024-12-11",
  "hire-gohighlevel-virtual-assistant-philippines": "2024-12-08",
  "ghl-automation-expert-philippines": "2024-12-05",
  "hire-technical-virtual-assistant-philippines": "2024-12-02",
};

/** Slugs covered by Batch 19 (Quality & Vetting / Sourcing Models / HubSpot
 * Operations cluster). These carried fabricated future dates (Sep 2026);
 * owner-verified publish dates 2025-06-10 → 2025-08-04 (5-day intervals
 * continuing Batch 18); dateModified via BATCH19_LASTUPDATED (2024-11-29 →
 * 2024-10-28, 3-day backward continuing Batch 18). All 12 are
 * hiring/sourcing-focused → homepage contextual link. */
export const BATCH19_SLUGS = new Set<string>([
  "secure-data-compliant-virtual-assistant",
  "high-tier-virtual-assistant-portfolio",
  "reliable-remote-operations-assistant",
  "english-fluent-virtual-assistant-philippines",
  "vetted-technical-virtual-assistant",
  "freelance-technical-virtual-assistant-direct",
  "hire-independent-contract-virtual-assistant",
  "remote-hubspot-operations-manager-philippines",
  "dedicated-virtual-assistant-no-agency-fee",
  "part-time-operations-virtual-assistant",
  "full-time-technical-remote-assistant",
  "hire-direct-virtual-assistant-philippines",
]);

/** Batch 19 dateModified overrides (3-day backward from 2024-11-29). */
export const BATCH19_LASTUPDATED: Record<string, string> = {
  "secure-data-compliant-virtual-assistant": "2024-11-29",
  "high-tier-virtual-assistant-portfolio": "2024-11-26",
  "reliable-remote-operations-assistant": "2024-11-23",
  "english-fluent-virtual-assistant-philippines": "2024-11-20",
  "vetted-technical-virtual-assistant": "2024-11-17",
  "freelance-technical-virtual-assistant-direct": "2024-11-14",
  "hire-independent-contract-virtual-assistant": "2024-11-11",
  "remote-hubspot-operations-manager-philippines": "2024-11-08",
  "dedicated-virtual-assistant-no-agency-fee": "2024-11-05",
  "part-time-operations-virtual-assistant": "2024-11-02",
  "full-time-technical-remote-assistant": "2024-10-31",
  "hire-direct-virtual-assistant-philippines": "2024-10-28",
};

/** Slugs covered by Batch 20 (Tech Stack Management / SaaS Onboarding / HubSpot
 * Admin / Zapier & n8n Automation / GHL & CRM Database / B2B Funnel / Premium
 * Contractor / HubSpot Expert / Executive VA cluster). These carried fabricated
 * future dates (Sep 2026); owner-verified publish dates 2025-08-09 → 2025-10-03
 * (5-day intervals continuing Batch 19); dateModified via BATCH20_LASTUPDATED
 * (2024-10-25 → 2024-09-22, 3-day backward continuing Batch 19). All 12 are
 * hiring-decision topics → homepage contextual link. */
export const BATCH20_SLUGS = new Set<string>([
  "tech-stack-management-freelancer",
  "saas-onboarding-virtual-assistant",
  "hubspot-certified-administrator-freelancer-philippines",
  "zapier-workflow-virtual-assistant",
  "n8n-automation-virtual-assistant",
  "hubspot-certified-virtual-assistant",
  "gohighlevel-specialist-virtual-assistant",
  "crm-database-virtual-assistant",
  "b2b-sales-funnel-virtual-assistant",
  "hire-premium-independent-remote-contractor",
  "technical-virtual-assistant-hubspot-expert",
  "executive-virtual-assistant-for-agency-owners",
]);

/** Batch 20 dateModified overrides (3-day backward from 2024-10-25). */
export const BATCH20_LASTUPDATED: Record<string, string> = {
  "tech-stack-management-freelancer": "2024-10-25",
  "saas-onboarding-virtual-assistant": "2024-10-22",
  "hubspot-certified-administrator-freelancer-philippines": "2024-10-19",
  "zapier-workflow-virtual-assistant": "2024-10-16",
  "n8n-automation-virtual-assistant": "2024-10-13",
  "hubspot-certified-virtual-assistant": "2024-10-10",
  "gohighlevel-specialist-virtual-assistant": "2024-10-07",
  "crm-database-virtual-assistant": "2024-10-04",
  "b2b-sales-funnel-virtual-assistant": "2024-10-01",
  "hire-premium-independent-remote-contractor": "2024-09-28",
  "technical-virtual-assistant-hubspot-expert": "2024-09-25",
  "executive-virtual-assistant-for-agency-owners": "2024-09-22",
};

/** Slugs covered by Batch 21 (Project Management VA / Specialized Automation VA /
 * Technical VA Freelance / Fractional Systems Manager / Operations VA /
 * Outsourcing Niches / HubSpot Specialist & Admin / Vetting / System Architect /
 * RevOps Outsourcing / GHL Architect cluster). These carried fabricated future
 * dates (Aug–Sep 2026); owner-verified publish dates 2025-10-08 → 2025-12-02
 * (5-day intervals continuing Batch 20); dateModified via BATCH21_LASTUPDATED
 * (2024-09-19 → 2024-08-17, 3-day backward continuing Batch 20). All 12 are
 * hiring-decision topics → homepage contextual link. */
export const BATCH21_SLUGS = new Set<string>([
  "project-management-virtual-assistant",
  "hire-specialized-automation-va",
  "technical-virtual-assistant-freelance",
  "fractional-systems-manager-remote",
  "hire-operations-virtual-assistant",
  "best-niches-for-outsourcing-high-level-technical-work-to-the-philippines",
  "hire-hubspot-specialist-philippines",
  "how-to-vet-advanced-technical-problem-solving-skills-remote-candidates",
  "hiring-a-remote-system-architect-in-the-philippines",
  "outsource-revenue-operations-revops-to-the-philippines",
  "hire-gohighlevel-architect-philippines",
  "how-to-hire-a-remote-hubspot-certified-administrator-in-the-philippines",
]);

/** Batch 21 dateModified overrides (3-day backward from 2024-09-19). */
export const BATCH21_LASTUPDATED: Record<string, string> = {
  "project-management-virtual-assistant": "2024-09-19",
  "hire-specialized-automation-va": "2024-09-16",
  "technical-virtual-assistant-freelance": "2024-09-13",
  "fractional-systems-manager-remote": "2024-09-10",
  "hire-operations-virtual-assistant": "2024-09-07",
  "best-niches-for-outsourcing-high-level-technical-work-to-the-philippines":
    "2024-09-04",
  "hire-hubspot-specialist-philippines": "2024-09-01",
  "how-to-vet-advanced-technical-problem-solving-skills-remote-candidates":
    "2024-08-29",
  "hiring-a-remote-system-architect-in-the-philippines": "2024-08-26",
  "outsource-revenue-operations-revops-to-the-philippines": "2024-08-23",
  "hire-gohighlevel-architect-philippines": "2024-08-20",
  "how-to-hire-a-remote-hubspot-certified-administrator-in-the-philippines":
    "2024-08-17",
};
